import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Typo, Flex, Icon } from '../../../ui';
import { MODULE_TYPES } from '../moduleConfig';
import theme from '../../../ui/theme';
import { toRgba } from '../../../ui/theme/colors';
import { useTranslation } from 'react-i18next';
// import CoursioLogo from '../../../ui/assets/coursio-logo.svg';
import ModuleTypeSelector from './ModuleTypeSelector';

// Context-specific translation keys for empty state messaging
const CONTEXT_TRANSLATIONS = {
  quiz: { title: 'editCourse.emptyQuizTitle', description: 'editCourse.emptyQuizDescription' },
  default: { title: 'editCourse.emptyPageTitle', description: 'editCourse.emptyPageDescription' },
};

// Main container with subtle, welcoming background
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  padding: 0 32px;
  width: 100%;
  text-align: center;
  transition: all 0.3s ease;
`;

// Card style matching ModuleTypeSelector
const ModuleTypeCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 12px;
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 8px;
  background: ${theme.colors.NEU_0};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover {
    border-color: ${theme.colors.PRIMARY};
    background: ${theme.colors.PRIMARY_5};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.1)};
  }

  &:active {
    transform: translateY(0);
  }
`;

/**
 * EmptyPagePlaceholder - Displays a welcoming empty state for pages with no modules
 *
 * @param {Object} props
 * @param {Function} props.onSelectModuleType - Callback when a module type is selected
 * @param {string[]} props.allowedModuleTypes - Optional array of module types to allow (e.g., ['task']). If not provided, shows all.
 * @param {string} props.defaultModuleType - If provided, shows a single add button that directly adds this module type
 * @param {string} props.contextType - Optional context type for customized messaging (e.g., 'quiz')
 * @returns {JSX.Element}
 */
const EmptyPagePlaceholder = ({ onSelectModuleType, allowedModuleTypes, defaultModuleType, contextType }) => {
  const { t } = useTranslation();

  // Get context-specific translation keys (scalable pattern for future contexts)
  const translationKeys = CONTEXT_TRANSLATIONS[contextType] || CONTEXT_TRANSLATIONS.default;
  const title = t(translationKeys.title);
  const description = t(translationKeys.description);

  const handleModuleTypeClick = (moduleType) => {
    if (onSelectModuleType) {
      onSelectModuleType(moduleType);
    }
  };

  // Memoize default module config lookup
  const defaultModuleConfig = useMemo(() => {
    if (!defaultModuleType) return null;
    return MODULE_TYPES.find(({ type }) => type === defaultModuleType);
  }, [defaultModuleType]);

  // If defaultModuleType is set, show simplified UI with single button
  if (defaultModuleType) {
    return (
      <Container data-module-picker>
        <Flex direction='column' gap='8' align='flex-start' width='100%'>
          <Typo.ContentSmallHeader>{title}</Typo.ContentSmallHeader>
          <Typo.Text>{description}</Typo.Text>
        </Flex>

        {defaultModuleConfig && (
          <ModuleTypeCard
            onClick={() => handleModuleTypeClick(defaultModuleType)}
            aria-label={t('editCourse.addModuleAriaLabel', { moduleType: t(defaultModuleConfig.labelKey) })}
          >
            {Icon[defaultModuleConfig.icon] && React.createElement(Icon[defaultModuleConfig.icon], { size: 'medium' })}
            <Typo.Info $caps>{t(defaultModuleConfig.labelKey)}</Typo.Info>
          </ModuleTypeCard>
        )}
      </Container>
    );
  }

  return (
    <Container data-module-picker>
      <Flex direction='column' gap='8' align='flex-start' width='100%'>
        <Typo.ContentSmallHeader color={theme.colors.NEU_PRIMARY}>{title}</Typo.ContentSmallHeader>
        <Typo.Text>{description}</Typo.Text>
      </Flex>

      <Flex direction='column' gap='12' align='flex-start' width='100%'>
        <Typo.SmallText>{t('editCourse.chooseModuleType')}</Typo.SmallText>
        <ModuleTypeSelector onSelectModuleType={handleModuleTypeClick} allowedModuleTypes={allowedModuleTypes} />
      </Flex>
    </Container>
  );
};

export default EmptyPagePlaceholder;
